import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { MeritBoxForm } from './MeritBoxForm';
import { PAGE_TEXT } from '../lang';
import {
  addApplicationItem,
  useGetMerit,
  useUpdateMerit,
} from '../../../graphql';
import { type MeritFormValues } from '../../../graphql/ApplicationForm';
import {
  Alert,
  type AlertDetails,
  FILE_MANAGE_ORDER,
  FilesPanel,
  Loading,
  type FileData,
  type SelectOption,
} from '../../../../../components';
import { handleFileRename } from '../../../../../utils/handleFileRename';
import { MissingSupportingDocumentsError } from '../../../../../utils/errors';

export type MeritBoxProps = {
  applicationNo: string;
  index: number;
  meritId: string;
  enableEdit: boolean;
  enableDelete: boolean;
  countryList: SelectOption[];
  handleDelete: () => void;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

const pageText = PAGE_TEXT.OTHER_INFORMATION.MERIT;
export const MeritBox: React.FunctionComponent<MeritBoxProps> = props => {
  const { applicationId } = useParams();
  const {
    applicationNo = '',
    meritId: initialMeritId = '',
    index: meritIndex,
    enableEdit,
    enableDelete,
    countryList,
    handleDelete,
    refetchApplication,
    handleLoading,
  } = props;

  const [meritId, setMeritId] = useState(initialMeritId);
  const [isCreateMode, setIsCreateMode] = useState(!initialMeritId);
  let initialMeritValues: MeritFormValues = {
    meritName: '',
    awardBodyName: '',
    awardDate: '',
    country: '',
    attainmentMeans: '',
  };

  const createMerit = addApplicationItem('merits', refetchApplication);
  const updateMerit = useUpdateMerit();
  const { meritFormData, loading, error } = useGetMerit(meritId);
  const { supportingDocuments, ...others } = meritFormData;
  if (Object.keys(others).length > 0) {
    initialMeritValues = { ...others };
  }

  let alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'error',
  };
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);

  const [filesArray, setFilesArray] = useState<FileData[]>(
    supportingDocuments ?? [],
  );

  useEffect(() => {
    setFilesArray(supportingDocuments ?? []);
  }, [loading]);

  const handleSubmit = async (values: MeritFormValues): Promise<void> => {
    const inputSupportingDocuments: FileData[] = filesArray.map(
      (doc, index) => {
        const { name, ...other } = doc;
        return {
          name: handleFileRename(applicationNo, 'merit', index + 1),
          ...other,
        };
      },
    );
    await handleLoading(async () => {
      try {
        if (inputSupportingDocuments.length <= 0) {
          throw new MissingSupportingDocumentsError(
            'You must provide at least one supporting document',
          );
        }
        // TODO "to" would be remove after backend update
        if (isCreateMode) {
          const res = await createMerit({
            variables: {
              id: applicationId,
              input: {
                merit: {
                  ...values,
                  supportingDocuments: inputSupportingDocuments,
                },
              },
            },
          });
          const resMeritId =
            res.data?.addApplicationItem.merits[meritIndex]._id;
          setMeritId(resMeritId);
          setIsCreateMode(!(resMeritId as string));
        } else {
          await updateMerit({
            variables: {
              id: meritId,
              ...values,
              supportingDocuments: inputSupportingDocuments,
            },
          });
        }
        alertDetailDef = {
          alertContent: 'Saved Merit Successfully.',
          alertType: 'success',
        };
      } catch (error: any) {
        if (error instanceof Error)
          alertDetailDef = {
            alertContent: `Saved Merit Failed. ${error.message}`,
            alertType: 'error',
          };
        if (error instanceof MissingSupportingDocumentsError)
          alertDetailDef = {
            alertContent: `${error.message}`,
            alertType: 'warning',
          };
      } finally {
        setAlertDetails(alertDetailDef);
      }
    });

    setOpenAlert(true);
  };

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.${(error as Error).message}`,
          alertType: 'error',
        }}
      />
    );
  }

  function handleFilesChange(order: number, id?: string): boolean {
    let newFilesArray = [...filesArray];
    switch (order) {
      case FILE_MANAGE_ORDER.UPLOAD:
        newFilesArray = [
          ...newFilesArray,
          {
            id: id as string,
            name: '',
            link: '',
          },
        ];
        break;
      case FILE_MANAGE_ORDER.REMOVE:
        newFilesArray = newFilesArray.filter(file => file.id !== id);
        break;
      default:
        break;
    }
    setFilesArray(newFilesArray);
    return true;
  }

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <MeritBoxForm
          initialValues={initialMeritValues}
          enableEdit={enableEdit}
          countryList={countryList}
          onSubmit={handleSubmit}
          enableDelete={enableDelete}
          handleDelete={handleDelete}
          meritIndex={meritIndex}
        />
        <div style={{ paddingTop: '15px', marginLeft: '1rem' }}>
          <FilesPanel
            disable={!enableEdit}
            disableDeleteOnly={!enableDelete}
            title={pageText.FILES_PANEL.PANEL_TITLE}
            caption={pageText.FILES_PANEL.PANEL_CAPTION}
            files={filesArray}
            handleFilesChange={handleFilesChange}
            handleLoading={handleLoading}
            maxFileNumber={5}
          />
        </div>
      </Container>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-top: 35px;

  .MuiGrid-item {
    width: 100%;
  }
`;
